/**
 * Sketch studio (product page). Concept A = sketch card in the buy box, concept B = studio dialog opened
 * from the gallery. Picked by `?studio=a|b` (review) or the section setting.
 *
 * Live mode (data-api + data-design, 2026-10-16): the photo, name, species and "human" word go to the HOM studio
 * service (POST {api}/sketch → worker: 3 sketches + QA gate, ~1 min); we poll {api}/sketch/{id} and show the sketches
 * that passed. The pick goes onto the live garment preview (hom-position.js setArt) and into the order
 * (properties Sketch, _sketch_key). Black garment → {api}/sketch/{id}/black?n= prepares the black-tee version.
 * Demo mode (no api/design): three pre-drawn sketches (The Greaser × Bjørn).
 */

const STEPS = [
  ['Reading the photo…', 1300],
  ['Matching the coat, ears and eyes…', 1600],
  ['Sketching the pose…', 1700],
  ['Inking the details…', 1300],
];
const LIVE_STEPS = [
  'Reading the photo…',
  'Matching the coat, ears and eyes…',
  'The painter is sketching the pose…',
  'Lettering the name…',
  'Checking every paw and letter…',
  'Preparing the white and black tee versions…',
  'Almost there…',
];
/** Rough wall time of 3 sketches + checks + black-tee versions (10-16 tests: 90–150 s); the countdown never shows 0. */
const LIVE_EST_S = 90;   // 10-19: black versions run alongside the white QA – 3 sketches land in ~65–95 s
const POLL_MS = 3000;
const PENDING_DB = 'hom-sketch';
const LEAD_KEY = 'hom-sketch-lead';

/**
 * The photo, name and words survive the trip to the Shopify sign-in page (6-digit email code) in IndexedDB,
 * so the client lands back on the product with everything filled in and the sketches start by themselves.
 */
function pendingDb() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(PENDING_DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore('pending');
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
/**
 * iOS Safari loses Blobs kept in IndexedDB: the File comes back, but fetch() sends an empty multipart body
 * (ARVI 2026-10-21: "This design can't be previewed yet" after signing in – the server saw no fields at all).
 * So the photo is stored as plain bytes and rebuilt into a fresh File on the way back.
 */
async function photoToBytes(file) {
  if (!(file instanceof Blob)) return null;
  return { buf: await file.arrayBuffer(), type: file.type || 'image/jpeg', name: file instanceof File && file.name ? file.name : 'photo.jpg' };
}
async function bytesToPhoto(p) {
  if (!p) return null;
  try {
    if (p.buf instanceof ArrayBuffer) return new File([p.buf], p.name || 'photo.jpg', { type: p.type || 'image/jpeg' });
    if (p instanceof Blob) return new File([await p.arrayBuffer()], 'photo.jpg', { type: p.type || 'image/jpeg' }); // older saves
  } catch {
    /* unreadable: the client adds the photo again */
  }
  return null;
}
async function pendingSet(value) {
  if (value && value.photo instanceof Blob) value = { ...value, photo: await photoToBytes(value.photo).catch(() => null) };
  const db = await pendingDb();
  await new Promise((res, rej) => {
    const tx = db.transaction('pending', 'readwrite');
    tx.objectStore('pending').put(value, 'draw');
    tx.oncomplete = () => res(undefined);
    tx.onerror = () => rej(tx.error);
  });
}
async function pendingTake() {
  const db = await pendingDb();
  return new Promise((res) => {
    const tx = db.transaction('pending', 'readwrite');
    const st = tx.objectStore('pending');
    const g = st.get('draw');
    g.onsuccess = () => {
      st.delete('draw');
      res(g.result || null);
    };
    g.onerror = () => res(null);
  });
}
function savedLead() {
  try {
    return JSON.parse(localStorage.getItem(LEAD_KEY) || 'null');
  } catch {
    return null;
  }
}
/** One free sketch round per device (ARVI 2026-10-18), then a free account. The server checks too – this only saves an upload. */
const VISITOR_KEY = 'hom_vid'; // site-wide visitor ID, made in layout/theme.liquid (ARVI 2026-10-19); older 'hom-sketch-vid' is migrated there
const FREE_USED_KEY = 'hom-sketch-free-used';
function visitorId() {
  try {
    let v = window.homVid || localStorage.getItem(VISITOR_KEY) || localStorage.getItem('hom-sketch-vid');
    if (!v) {
      v = (crypto.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`).toLowerCase();
      localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return '';
  }
}
/** Meta's own cookies for the server-side SketchCreated / Lead events (2026-10-19): the pixel's _fbp and _fbc, or _fbc from ?fbclid. */
function fbCookies() {
  const out = { fbp: '', fbc: '', page: location.href.slice(0, 500) };
  try {
    for (const part of document.cookie.split(';')) {
      const [k, ...v] = part.trim().split('=');
      if (k === '_fbp') out.fbp = v.join('=');
      if (k === '_fbc') out.fbc = v.join('=');
    }
    if (!out.fbc) {
      const id = new URLSearchParams(location.search).get('fbclid');
      if (id) out.fbc = `fb.1.${Date.now()}.${id}`;
    }
  } catch {
    /* no cookie access – the server still has IP and user agent */
  }
  return out;
}
/** Triple Whale custom events (2026-10-20, Triple Pixel app embed): SketchCreated / Lead – so TW shows cost per sketch per ad. No-op without the pixel. */
function twEvent(name, data) {
  try {
    if (typeof window.TriplePixel === 'function') window.TriplePixel('custom', name, { vid: visitorId(), ...data });
  } catch {
    /* tracking must never break the studio */
  }
}
function freeUsed() {
  try {
    return localStorage.getItem(FREE_USED_KEY) === '1';
  } catch {
    return false;
  }
}
function markFreeUsed() {
  try {
    localStorage.setItem(FREE_USED_KEY, '1');
  } catch {
    /* private mode: the server still answers 401 */
  }
}
function clearFreeUsed() {
  try {
    localStorage.removeItem(FREE_USED_KEY);
  } catch {
    /* private mode */
  }
}
class NeedAccount extends Error {}
/** The client wants another photo after the "we see a cat" question – no sketches were drawn, nothing is used up. */
class ChangePhoto extends Error {}
/** The client got no sketches (time out, job error, lost connection) – reported to the studio so we hear about it at once. */
class StudioFail extends Error {
  constructor(msg, why) {
    super(msg);
    this.why = why;
  }
}
const TIMEOUT_MS = 5 * 60 * 1000;

class HomSketchStudio extends HTMLElement {
  #ac = new AbortController();
  #root = null;
  #mode = 'off';
  #tries = 1; // free rounds left on this device; Infinity with an account
  #picked = 0;
  #sketches = [];
  #photoUrl = '';
  #running = false;
  #api = '';
  #design = '';
  #preparing = false;
  #info = null;
  #job = null; // { id, t }
  #black = {}; // n → 'running' | 'done' | 'error'
  #askResolve = null; // "we see a cat – draw it?" waiting for the client's click
  #blackTimer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#root = this.closest('hom-product');
    if (!this.#root) return;

    const asked = (new URLSearchParams(window.location.search).get('studio') ?? '').toLowerCase();
    this.#mode = asked === 'a' || asked === 'b' ? asked : this.#root.dataset.studio || 'off';
    if (this.dataset.noPet === 'true') this.#mode = 'off';
    if (this.#mode === 'off') return;
    this.#root.dataset.studio = this.#mode;
    for (const el of this.#root.querySelectorAll(this.#mode === 'a' ? '[data-sk-a]' : '[data-sk-b]')) el.hidden = false;

    this.#api = (this.dataset.api || '').replace(/\/+$/, '');
    this.#design = this.dataset.design || '';
    if (!this.#live) {
      const demo = (k) => (this.dataset[k] || '').split('|').filter(Boolean);
      const art = demo('demo');
      const men = demo('demoMen');
      const women = demo('demoWomen');
      const cut = demo('demoCut');
      this.#sketches = art.map((a, i) => ({ n: i + 1, art: a, cut: cut[i] || '', tee: { Men: men[i] || men[0], Women: women[i] || women[0], Kids: women[i] || women[0] } }));
      this.#tries = 3;
    } else {
      for (const el of this.#all('[data-sk-demo-note], [data-sk-action="email"]')) el.hidden = true;
    }

    this.#root.addEventListener('click', this.#onClick, { signal });
    this.#root.addEventListener('input', this.#onInput, { signal });
    this.#root.addEventListener('change', this.#onChange, { signal });
    this.#q('[data-sk-acct]', this)?.addEventListener('submit', this.#onAccount, { signal });
    this.#syncArt();
    this.#syncName();
    if (this.#live) {
      this.#tries = this.#hasAccount() ? Infinity : freeUsed() ? 0 : 1;
      this.#renderTries();
    }
    if (this.#live && this.dataset.customer) this.#resume();
    if (this.#live) this.#loadFromUrl();
  }

  /**
   * Reopened from "Your sketches" in the favourites tray (ARVI 2026-10-19): ?sk=<job>.<token>.<n> → the sketches come back,
   * the picked one goes on the tee, the photo and the name go back into the order form. Nothing is drawn again.
   */
  async #loadFromUrl() {
    const url = new URL(window.location.href);
    const m = /^(j\d{8}-\d{6}-[a-z0-9]{4})\.([a-f0-9]{24})\.([123])$/.exec(url.searchParams.get('sk') || '');
    if (!m) return;
    const [, id, t, nStr] = m;
    const n = Number(nStr);
    const signal = this.#ac.signal;
    try {
      const s = await (await fetch(`${this.#api}/sketch/${id}?t=${t}`, { cache: 'no-store', signal })).json();
      if (s.state !== 'done' || !s.sketches?.includes(n)) return;
      this.#job = { id, t };
      this.#black = {};
      for (const [k, v] of Object.entries(s.black || {})) if (v === 'done') this.#black[Number(k)] = 'done';
      this.#sketches = s.sketches.map((k) => ({ n: k, art: this.#fileUrl(k, 'web'), cut: this.#fileUrl(k, 'web') }));
      // the photo of that round goes back into the order form (the order needs the file)
      try {
        const blob = await (await fetch(`${this.#api}/file/${id}/photo.jpg?t=${t}`, { signal })).blob();
        const input = this.#q('[data-sk-file]', this);
        if (input instanceof HTMLInputElement && typeof DataTransfer !== 'undefined') {
          const dt = new DataTransfer();
          dt.items.add(new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
          input.files = dt.files;
          this.#onFile(input);
        }
      } catch {
        /* the sketch still shows; the client adds the photo again before the cart */
      }
      const nameIn = this.#q('[data-sk-nameinput]', this);
      if (nameIn instanceof HTMLInputElement && s.name) {
        nameIn.value = s.name;
        this.#syncName(nameIn);
      }
      const capIn = this.#q('[data-sk-capinput]', this);
      if (capIn instanceof HTMLInputElement && s.caption) capIn.value = s.caption;
      if (s.sex) for (const b of this.#all('[data-sk-sexv]', this)) b.setAttribute('aria-pressed', String(b.dataset.skSexv === s.sex));
      const prog = this.#q('[data-sk-prog]', this);
      const res = this.#q('[data-sk-res]', this);
      if (prog instanceof HTMLElement) prog.hidden = true;
      this.#renderGrid(this.#q('[data-sk-grid]', this));
      if (res instanceof HTMLElement) res.hidden = false;
      this.#stage('result');
      this.#pick(n, this.#q('[data-sk-grid]', this));
      this.#use();
      url.searchParams.delete('sk');
      history.replaceState(history.state, '', url.toString());
    } catch {
      /* no studio – the page works as usual */
    }
  }

  disconnectedCallback() {
    this.#ac.abort();
    clearTimeout(this.#blackTimer);
  }

  get #live() {
    return Boolean(this.#api && this.#design);
  }

  #q(sel, scope = this.#root) {
    return scope ? scope.querySelector(sel) : null;
  }

  #all(sel, scope = this.#root) {
    return scope ? [...scope.querySelectorAll(sel)] : [];
  }

  // ---------- events ----------

  #onClick = (e) => {
    const t = e.target instanceof Element ? e.target.closest('button') : null;
    if (!t) return;
    const action = t.dataset.skAction;
    if (action === 'open') return this.#open(t.dataset.skTo);
    if (action === 'close') return this.#close();
    if (action === 'draw' || action === 'redraw') return this.#draw();
    if (action === 'back') return this.#stage('form');
    if (action === 'email') return this.#email(t);
    if (action === 'use') return this.#use();
    if (t.dataset.skPick) return this.#pick(Number(t.dataset.skPick), t.closest('[data-sk-grid]'));
    if (t.dataset.skViewmode) return this.#view(t.dataset.skViewmode);
    if (t.dataset.skSexv) {
      for (const b of this.#all('[data-sk-sexv]', this)) b.setAttribute('aria-pressed', String(b === t));
    }
    if (t.dataset.skSpeciesv) {
      for (const b of this.#all('[data-sk-speciesv]', this)) b.setAttribute('aria-pressed', String(b === t));
      this.#setPetProperty(t.dataset.skSpeciesv);
    }
    if (t.dataset.skAsk && this.#askResolve) {
      const r = this.#askResolve;
      this.#askResolve = null;
      r(t.dataset.skAsk === 'draw');
    }
    // colour changed in the buy box: the picked sketch follows (black tee needs its own version)
    if (t.dataset.colour && this.#picked && this.#q('[data-sk-view] [aria-pressed="true"]')?.getAttribute('data-sk-viewmode') === 'sketch') {
      setTimeout(() => this.#showArt(), 0);
    }
  };

  #onInput = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.hasAttribute('data-name') || t.hasAttribute('data-sk-nameinput')) this.#syncName(t);
  };

  #onChange = (e) => {
    const t = e.target;
    if (!(t instanceof HTMLInputElement)) return;
    if (t.hasAttribute('data-sk-file')) this.#onFile(t);
  };

  // ---------- name sync (buy box ⇄ dialog) ----------

  /**
   * The picture in the dialog = the product photo of the cut × colour chosen in the buy box (ARVI 2026-10-18:
   * a white tee picked, a black one shown – not any more). The gallery's front thumb is that photo at 160 px;
   * the main image is used when it already shows the front.
   */
  #syncArt() {
    const art = this.querySelector('[data-sk-art-fallback]');
    if (!(art instanceof HTMLImageElement)) return;
    const main = this.#root.querySelector('[data-main-img]');
    const frontBtn = this.#root.querySelector('[data-thumbs] [data-thumb="front"]');
    const frontThumb = frontBtn?.querySelector('img');
    let src = '';
    if (frontThumb instanceof HTMLImageElement && frontThumb.getAttribute('src')) src = frontThumb.getAttribute('src').replace('width=160', 'width=800');
    else if (main instanceof HTMLImageElement) src = main.currentSrc || main.src;
    if (src && art.getAttribute('src') !== src) art.src = src;
  }

  #syncName(source) {
    const main = this.#q('[data-name]');
    const dlg = this.#q('[data-sk-nameinput]', this);
    if (source && main instanceof HTMLInputElement && dlg instanceof HTMLInputElement) {
      if (source === main && dlg.value !== main.value) dlg.value = main.value;
      if (source === dlg && main.value !== dlg.value) {
        main.value = dlg.value;
        main.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    const name = (main instanceof HTMLInputElement && main.value.trim()) || '';
    const pet = this.dataset.pet || 'dog';
    for (const el of this.#all('[data-sk-name]')) el.textContent = name || `your ${pet}`;
    const title = this.#root?.dataset.productTitle || 'this one';
    for (const el of this.#all('[data-sk-title]')) el.textContent = title;
  }

  // ---------- design info (live) ----------

  async #loadInfo() {
    if (!this.#live || this.#info) return;
    try {
      const r = await fetch(`${this.#api}/design/${encodeURIComponent(this.#design)}`, { signal: this.#ac.signal });
      if (!r.ok) throw new Error(String(r.status));
      this.#info = await r.json();
    } catch {
      this.#info = { human: false, unavailable: true };
    }
    this.#renderWords();
  }

  /** "Words on the print": the design's own text stays; only the human word (Papa / Mamma / a name) is editable. */
  #renderWords() {
    const info = this.#info;
    if (!info) return;
    const rule = this.#q('[data-sk-rule]', this);
    const cap = this.#q('[data-sk-capinput]', this);
    if (info.unavailable) {
      this.#err('Sketches for this design are coming soon. Order now – you approve the final portrait by email.');
      return;
    }
    if (rule) {
      const words = info.text ? `<i>„${escapeHtml(info.text.replaceAll(' / ', ' '))}“</i>` : '';
      rule.innerHTML = info.human
        ? `Your pet's name goes where <b>${escapeHtml(info.sample_name || 'the name')}</b> is. Who is the human in the joke? ${words}`
        : `The words stay as in the design ${words}; your pet's name goes where <b>${escapeHtml(info.sample_name || 'the name')}</b> is.`;
    }
    if (cap instanceof HTMLInputElement) {
      cap.hidden = !info.human;
      cap.maxLength = 24;
      cap.placeholder = info.pronoun_only ? 'Papa or Mamma (he / she)' : `e.g. ${info.human_default || 'Papa'}, Mamma, Anna`;
      cap.setAttribute('aria-label', 'Who is the human?');
      if (info.human && !cap.value) cap.value = info.human_default || '';
    }
  }

  // ---------- B · dialog ----------

  /** `stage` = 'form' from the buy-box "Change" button (ARVI 2026-10-19: it opened on the sketches with nowhere to change the photo). */
  #open(stage) {
    const d = this.#q('[data-sk-dialog]', this);
    if (!(d instanceof HTMLDialogElement)) return;
    if (stage === 'form' || stage === 'result') this.#stage(stage);
    // prefill from the buy box
    const sex = /** @type {HTMLInputElement|null} */ (this.#q('[data-sex]:checked'))?.value || 'Male';
    for (const b of this.#all('[data-sk-sexv]', this)) b.setAttribute('aria-pressed', String(b.dataset.skSexv === sex));
    const cap = this.#q('[data-caption]');
    const capIn = this.#q('[data-sk-capinput]', this);
    if (cap instanceof HTMLInputElement && capIn instanceof HTMLInputElement && cap.value) capIn.value = cap.value;
    this.#syncName();
    this.#syncArt();
    this.#loadInfo();
    d.showModal();
    this.#q('[data-sk-stage="form"]', this)?.scrollIntoView({ block: 'start' });
  }

  // ---------- free account (Shopify customer accounts, email code) ----------

  /** Signed in with Shopify (the email is confirmed by the sign-in code). ARVI 2026-10-21: no mobile number needed for now. */
  #hasAccount() {
    return Boolean(this.dataset.customer);
  }

  #askAccount() {
    const d = this.#q('[data-sk-dialog]', this);
    if (d instanceof HTMLDialogElement && !d.open) this.#open();
    const f = this.#q('[data-sk-acct]', this);
    if (f instanceof HTMLFormElement) {
      const lead = savedLead() || {};
      const set = (n, v) => {
        const el = f.elements.namedItem(n);
        if (el instanceof HTMLInputElement && !el.value && v) el.value = v;
      };
      set('email', this.dataset.customerEmail || lead.email);
      const btn = this.#q('[data-sk-acct-go]', f);
      if (btn) btn.textContent = this.dataset.customer ? 'Draw my sketches →' : 'Send my code →';
    }
    this.#stage('account');
    /** @type {HTMLInputElement|null} */ (this.#q('[data-sk-acct] input:invalid, [data-sk-acct] input', this))?.focus({ preventScroll: true });
  }

  #onAccount = async (e) => {
    e.preventDefault();
    const f = e.currentTarget;
    if (!(f instanceof HTMLFormElement)) return;
    const val = (n) => {
      const el = f.elements.namedItem(n);
      return el instanceof HTMLInputElement ? el.value.trim() : '';
    };
    const errEl = this.#q('[data-sk-acct-err]', f);
    const fail = (msg) => {
      if (errEl instanceof HTMLElement) {
        errEl.textContent = msg;
        errEl.hidden = !msg;
      }
    };
    // ARVI 2026-10-21: email only for now (the mobile field is gone, more leads)
    const lead = { email: val('email').toLowerCase() };
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(lead.email)) return fail('Please check your email address.');
    fail('');
    const btn = this.#q('[data-sk-acct-go]', f);
    if (btn instanceof HTMLButtonElement) btn.disabled = true;
    try {
      const body = new FormData();
      for (const [k, v] of Object.entries(lead)) body.append(k, v);
      const news = f.elements.namedItem('news');
      body.append('news', news instanceof HTMLInputElement && news.checked ? '1' : '0');
      body.append('design', this.#design);
      body.append('customer', this.dataset.customer || '');
      body.append('visitor', visitorId());
      for (const [k, v] of Object.entries(fbCookies())) body.append(k, v);
      const r = await fetch(`${this.#api}/lead`, { method: 'POST', body, signal: this.#ac.signal });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Something went wrong. Please try again.');
      twEvent('Lead', { design: this.#design });
      try {
        localStorage.setItem(LEAD_KEY, JSON.stringify({ ...lead, customer: this.dataset.customer || '', ts: Date.now() }));
      } catch {
        /* private mode: the account still works, we ask again next time */
      }
      if (this.dataset.customer) {
        // already signed in – only the missing details were asked. ARVI 2026-10-19: do NOT start drawing by itself –
        // back to the sketches (or the form) with "Draw 3 more" ready; the client decides.
        this.#tries = Infinity;
        if (btn instanceof HTMLButtonElement) btn.disabled = false;
        this.#renderTries();
        this.#stage(this.#sketches.length ? 'result' : 'form');
        return;
      }
      // keep the photo and words, sign in with the email code, come back here
      const nameIn = this.#q('[data-sk-nameinput]', this);
      const capIn = this.#q('[data-sk-capinput]', this);
      await pendingSet({
        design: this.#design,
        photo: this.#photo(),
        name: nameIn instanceof HTMLInputElement ? nameIn.value.trim() : '',
        human: capIn instanceof HTMLInputElement ? capIn.value.trim() : '',
        species: this.#pressed('[data-sk-speciesv]', 'skSpeciesv', this.dataset.pet || 'dog'),
        sex: this.#pressed('[data-sk-sexv]', 'skSexv', 'Male'),
        ts: Date.now(),
      }).catch(() => undefined);
      const back = new URL(window.location.href);
      back.searchParams.set('sk', 'resume');
      const login = new URL(this.dataset.login || '/customer_authentication/login', window.location.origin);
      login.searchParams.set('return_to', back.pathname + back.search);
      login.searchParams.set('locale', 'en');
      login.searchParams.set('login_hint', lead.email);
      window.location.assign(login.toString());
    } catch (err) {
      if (this.#ac.signal.aborted) return;
      fail(err instanceof Error ? err.message : String(err));
      if (btn instanceof HTMLButtonElement) btn.disabled = false;
    }
  };

  /** Back from the Shopify sign-in: restore the photo and words, then draw. */
  async #resume() {
    let p = null;
    try {
      p = await pendingTake();
    } catch {
      return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.has('sk')) {
      url.searchParams.delete('sk');
      history.replaceState(history.state, '', url.toString());
    }
    if (!p || p.design !== this.#design || Date.now() - (p.ts || 0) > 2 * 3600 * 1000) return;
    this.#open();
    const file = await bytesToPhoto(p.photo);
    const input = this.#q('[data-sk-file]', this);
    if (file && input instanceof HTMLInputElement && typeof DataTransfer !== 'undefined') {
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      this.#onFile(input);
    }
    const nameIn = this.#q('[data-sk-nameinput]', this);
    if (nameIn instanceof HTMLInputElement && p.name) {
      nameIn.value = p.name;
      this.#syncName(nameIn);
    }
    const capIn = this.#q('[data-sk-capinput]', this);
    if (capIn instanceof HTMLInputElement && p.human) capIn.value = p.human;
    for (const b of this.#all('[data-sk-speciesv]', this)) b.setAttribute('aria-pressed', String(b.dataset.skSpeciesv === p.species));
    for (const b of this.#all('[data-sk-sexv]', this)) b.setAttribute('aria-pressed', String(b.dataset.skSexv === p.sex));
    if (this.#hasAccount()) this.#draw();
    else this.#askAccount();
  }

  #close() {
    const d = this.#q('[data-sk-dialog]', this);
    if (d instanceof HTMLDialogElement && d.open) d.close();
  }

  #stage(which) {
    const form = this.#q('[data-sk-stage="form"]', this);
    const result = this.#q('[data-sk-stage="result"]', this);
    const acct = this.#q('[data-sk-stage="account"]', this);
    if (form instanceof HTMLElement) form.hidden = which !== 'form';
    if (result instanceof HTMLElement) result.hidden = which !== 'result';
    if (acct instanceof HTMLElement) acct.hidden = which !== 'account';
    this.#q('.sk-dlg-in', this)?.scrollTo({ top: 0 });
  }

  #err(msg) {
    const el = this.#q('[data-sk-err]', this);
    if (!(el instanceof HTMLElement)) return;
    el.textContent = msg || '';
    el.hidden = !msg;
  }

  /** The photo chosen in the dialog, else the one already in the buy box. */
  #photo() {
    for (const sel of ['[data-sk-file]', '[data-photo-input]']) {
      const el = this.#q(sel);
      if (el instanceof HTMLInputElement && el.files?.[0]) return el.files[0];
    }
    return null;
  }

  #onFile(input) {
    const file = input.files?.[0];
    const img = this.#q('[data-sk-thumb]', this);
    const ic = this.#q('[data-sk-drop-ic]', this);
    const nm = this.#q('[data-sk-drop-name]', this);
    if (!file) return;
    this.#err('');
    if (nm) nm.textContent = file.name;
    if (img instanceof HTMLImageElement && /image\/(jpeg|png|webp)/.test(file.type)) {
      if (this.#photoUrl) URL.revokeObjectURL(this.#photoUrl);
      this.#photoUrl = URL.createObjectURL(file);
      img.src = this.#photoUrl;
      img.hidden = false;
      if (ic instanceof HTMLElement) ic.hidden = true;
    }
    // hand the same file to the buy-box upload so the order carries it
    const main = this.#q('[data-photo-input]');
    if (main instanceof HTMLInputElement && typeof DataTransfer !== 'undefined') {
      try {
        const dt = new DataTransfer();
        dt.items.add(file);
        main.files = dt.files;
        main.dispatchEvent(new Event('change', { bubbles: true }));
      } catch {
        /* older browsers: the client re-uploads in the buy box */
      }
    }
  }

  #pressed(sel, key, fallback) {
    const b = this.#all(sel, this).find((x) => x.getAttribute('aria-pressed') === 'true');
    return (b && b.dataset[key]) || fallback;
  }

  /** Dialog values → buy box, then close and show the pick in the gallery. */
  #use() {
    if (!this.#picked) return;
    // the pick is remembered on the server – "Your sketches" in the favourites tray shows picked ones only (ARVI 2026-10-19)
    if (this.#job && !this.#sketches[this.#picked - 1]?.saved) {
      const body = new FormData();
      body.append('t', this.#job.t);
      body.append('n', String(this.#picked));
      fetch(`${this.#api}/sketch/${this.#job.id}/pick`, { method: 'POST', body, keepalive: true }).catch(() => undefined);
      this.#sketches[this.#picked - 1].saved = true;
    }
    const sex = this.#pressed('[data-sk-sexv]', 'skSexv', 'Male');
    for (const r of this.#all('[data-sex]')) {
      if (r instanceof HTMLInputElement) {
        r.checked = r.value === sex;
        if (r.checked) r.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    const capIn = this.#q('[data-sk-capinput]', this);
    const cap = this.#q('[data-caption]');
    if (capIn instanceof HTMLInputElement && cap instanceof HTMLInputElement && !capIn.hidden) {
      cap.value = capIn.value;
      cap.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.#close();
    this.#apply();
    // after "Use sketch" the client sees the gallery with their sketch on the tee, not the CTA (ARVI 2026-10-18)
    requestAnimationFrame(() => this.#toGallery());
  }

  /** Scroll so the gallery top sits just under the sticky header. */
  #toGallery() {
    const gal = this.#q('.gal');
    if (!(gal instanceof HTMLElement)) return;
    const head = document.querySelector('#header-component');
    const offset = head instanceof HTMLElement ? head.getBoundingClientRect().bottom : 0;
    const top = gal.getBoundingClientRect().top + window.scrollY - Math.max(0, offset) - 8;
    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
  }

  // ---------- drawing ----------

  async #draw() {
    if (this.#running) return;
    if (this.#tries <= 0 && !this.#live) return;
    const inDialog = this.#mode === 'b';

    let body = null;
    if (this.#live) {
      const photo = this.#photo();
      const nameIn = this.#q(inDialog ? '[data-sk-nameinput]' : '[data-name]', inDialog ? this : this.#root);
      const name = nameIn instanceof HTMLInputElement ? nameIn.value.trim() : '';
      if (!photo) return this.#err(`Add a photo of your ${this.dataset.pet || 'pet'} first.`);
      if (!name) {
        if (nameIn instanceof HTMLInputElement) nameIn.focus();
        return this.#err('Write the name first – it goes on the design.');
      }
      if (this.#info?.unavailable) return;
      this.#err('');
      // read the photo into fresh bytes first: a File restored from storage / another page can be unreadable on iOS,
      // and then Safari sends an empty form (2026-10-21) – better to ask for the photo again than to fail on the server
      if (this.#preparing) return; // double tap while the photo is being read
      this.#preparing = true;
      let fresh = null;
      try {
        fresh = await bytesToPhoto(await photoToBytes(photo).catch(() => null));
      } finally {
        this.#preparing = false;
      }
      if (this.#running) return;
      if (!fresh) {
        for (const sel of ['[data-sk-file]', '[data-photo-input]']) {
          const el = this.#q(sel);
          if (el instanceof HTMLInputElement) el.value = '';
        }
        return this.#err(`We couldn't read that photo. Please add the photo of your ${this.dataset.pet || 'pet'} again.`);
      }
      const capIn = this.#q('[data-sk-capinput]', this);
      body = new FormData();
      body.append('design', this.#design);
      body.append('photo', fresh, fresh.name || 'photo.jpg');
      body.append('name', name);
      body.append('human', capIn instanceof HTMLInputElement && !capIn.hidden ? capIn.value.trim() : '');
      body.append('species', this.#pressed('[data-sk-speciesv]', 'skSpeciesv', this.dataset.pet || 'dog'));
      body.append('sex', this.#pressed('[data-sk-sexv]', 'skSexv', 'Male'));
      body.append('colour', this.#colour());
      body.append('product', this.#root?.dataset.productTitle || '');
      // ARVI 2026-10-18: one free round without an account, then a free account (Shopify email code + mobile) – unlimited
      const account = this.#hasAccount();
      if (!account && this.#tries <= 0) return this.#askAccount();
      body.append('customer', account ? this.dataset.customer || '' : '');
      body.append('visitor', visitorId());
      for (const [k, v] of Object.entries(fbCookies())) body.append(k, v);
    }

    if (inDialog) this.#stage('result');
    const scope = inDialog ? this : this.#q('[data-sk-a]');
    const prog = this.#q('[data-sk-prog]', scope);
    const res = this.#q('[data-sk-res]', scope);
    const status = this.#q('[data-sk-status]', scope);
    const fail = this.#q('[data-sk-fail]', scope);
    const drawBtn = this.#q('[data-sk-action="draw"]', scope);
    if (!(prog instanceof HTMLElement) || !(res instanceof HTMLElement)) return;

    this.#running = true;
    const started = Date.now();
    this.#picked = 0;
    res.hidden = true;
    if (fail instanceof HTMLElement) fail.hidden = true;
    prog.hidden = false;
    if (drawBtn instanceof HTMLButtonElement) drawBtn.disabled = true;
    const use = this.#q('[data-sk-action="use"]', this);
    if (use instanceof HTMLButtonElement) {
      use.disabled = true;
      use.textContent = 'Pick a sketch to continue';
    }

    try {
      if (this.#live) {
        this.#sketches = await this.#drawLive(body, status);
      } else {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        for (const [txt, ms] of STEPS) {
          if (status) status.textContent = txt;
          await new Promise((r) => setTimeout(r, reduced ? 250 : ms));
          if (this.#ac.signal.aborted) return;
        }
        this.#tries -= 1;
      }
      prog.hidden = true;
      this.#renderGrid(this.#q('[data-sk-grid]', scope));
      res.hidden = false;
      this.#q('.sk-pick', scope)?.focus({ preventScroll: true });
    } catch (e) {
      if (this.#ac.signal.aborted) return;
      prog.hidden = true;
      if (e instanceof NeedAccount) return this.#askAccount();
      if (e instanceof ChangePhoto) {
        // nothing was drawn and the server does not count it – give the free round back
        if (this.#tries !== Infinity) {
          this.#tries = Math.max(this.#tries, 1);
          clearFreeUsed();
        }
        if (inDialog) return this.#stage('form');
        return this.#err(`Choose another photo of your ${this.dataset.pet || 'pet'} above, then draw again.`);
      }
      const msg = e instanceof Error ? e.message : String(e);
      if (e instanceof StudioFail) this.#report(e.why, msg, Math.round((Date.now() - started) / 1000));
      if (fail instanceof HTMLElement) {
        const m = this.#q('[data-sk-fail-msg]', fail);
        if (m) m.textContent = msg;
        fail.hidden = false;
      } else {
        this.#err(msg);
      }
    } finally {
      this.#running = false;
      if (drawBtn instanceof HTMLButtonElement) drawBtn.disabled = false;
      this.#renderTries();
    }
  }

  /** POST the job, then poll until the sketches are ready. Returns [{n, art, cut}]. */
  async #drawLive(body, status) {
    const signal = this.#ac.signal;
    let r;
    try {
      r = await fetch(`${this.#api}/sketch`, { method: 'POST', body, signal });
    } catch {
      throw new StudioFail('No connection to our studio. Check the internet and try again.', 'network');
    }
    const j = await r.json().catch(() => ({}));
    if (r.status === 401) {
      // the free round is used (this device or this network) – the account form takes over
      this.#tries = 0;
      markFreeUsed();
      throw new NeedAccount(j.error || '');
    }
    if (!r.ok) throw new Error(j.error || 'Something went wrong. Please try again.');
    this.#tries = j.account ? Infinity : Math.max(0, Number(j.left ?? 0));
    if (!j.account && this.#tries <= 0) markFreeUsed();
    this.#job = { id: j.id, t: j.t };
    this.#black = {};

    let t0 = Date.now();
    let step = 0;
    let line = LIVE_STEPS[0];
    let ready = '';
    // countdown so the client sees the studio is working (ARVI 2026-10-16): "… · about 95 s"
    const tick = () => {
      if (!status) return;
      const left = Math.round(LIVE_EST_S - (Date.now() - t0) / 1000);
      status.textContent = left > 5 ? `${line} · about ${left} s` : left > -30 ? `${line} · a few more seconds` : 'Taking a little longer – the painter is redoing a detail so every paw and letter is right…';
    };
    const timer = window.setInterval(tick, 1000);
    try {
      while (Date.now() - t0 < TIMEOUT_MS) {
        line = ready || LIVE_STEPS[Math.min(step, LIVE_STEPS.length - 1)];
        tick();
        step += 1;
        await new Promise((res) => setTimeout(res, POLL_MS * (step < 3 ? 1 : 2)));
        if (signal.aborted) throw new Error('aborted');
        let s;
        try {
          const pr = await fetch(`${this.#api}/sketch/${j.id}?t=${j.t}`, { signal, cache: 'no-store' });
          s = await pr.json();
        } catch {
          continue; // a missed poll is fine
        }
        if (s.state === 'error') throw new StudioFail(s.error || 'We couldn’t draw this one. Try another photo.', 'error');
        if (s.state === 'cancelled') throw new ChangePhoto();
        if (s.state === 'ask' && s.ask) {
          // ARVI 2026-10-19: the photo decides the species – a cat on a "dog" design is fine, but we ask first
          window.clearInterval(timer);
          const draw = await this.#askSpecies(s.ask, status);
          if (signal.aborted) throw new Error('aborted');
          const fd = new FormData();
          fd.append('t', j.t);
          fd.append('action', draw ? 'draw' : 'cancel');
          let ok = false;
          try {
            const ar = await fetch(`${this.#api}/sketch/${j.id}/species`, { method: 'POST', body: fd, signal });
            ok = ar.ok;
          } catch {
            ok = false;
          }
          if (!draw) throw new ChangePhoto();
          if (!ok) throw new StudioFail('No connection to our studio. Check the internet and try again.', 'network');
          this.#setPetProperty(s.ask.seen, s.ask.what);
          t0 = Date.now();
          step = 0;
          line = LIVE_STEPS[0];
          window.setInterval(tick, 1000);
          continue;
        }
        if (s.state === 'running' && s.done) {
          ready = `${s.done} of 3 sketches ready (white + black)…`;
          line = ready;
          step = Math.max(step, 4);
          tick();
        }
        if (s.state === 'done') {
          if (!s.sketches?.length) throw new StudioFail('We couldn’t draw this one. Try another photo – or order and our artist draws it by hand.', 'empty');
          // black-tee versions are made with the sketches – the garment switches colour instantly
          for (const [k, v] of Object.entries(s.black || {})) if (v === 'done') this.#black[Number(k)] = 'done';
          twEvent('SketchCreated', { design: this.#design, job: this.#job?.id || '', sketches: s.sketches.length });
          return s.sketches.map((n) => ({ n, art: this.#fileUrl(n, 'web'), cut: this.#fileUrl(n, 'web') }));
        }
      }
      throw new StudioFail('This is taking longer than usual. We’ll keep drawing – try again in a minute.', 'timeout');
    } finally {
      window.clearInterval(timer);
    }
  }

  /**
   * "We see a cat – draw the cat?" Shown inside the progress block; resolves true (draw) or false (change the photo).
   * @param {{seen: string, what: string, chosen: string}} ask
   */
  #askSpecies(ask, status) {
    const prog = status instanceof HTMLElement ? status.closest('[data-sk-prog]') : null;
    const box = prog ? prog.querySelector('[data-sk-ask]') : null;
    if (!(box instanceof HTMLElement)) return Promise.resolve(true);   // old markup: just draw what's in the photo
    const seen = ask.seen === 'dog' || ask.seen === 'cat' ? ask.seen : (ask.what || 'pet').toLowerCase();
    const what = ask.what || seen;
    const chosen = ask.chosen || this.dataset.pet || '';
    const msg = box.querySelector('[data-sk-ask-msg]');
    if (msg) {
      msg.textContent = chosen && chosen !== seen
        ? `We see a ${what} in the photo, but “${chosen}” was chosen. Draw the ${seen} from the photo?`
        : `We see a ${what} in the photo. Draw it?`;
    }
    const yes = box.querySelector('[data-sk-ask="draw"]');
    if (yes) yes.textContent = `Yes, draw the ${seen} →`;
    if (status) status.hidden = true;
    const frames = prog?.querySelector('.sk-frames');
    if (frames instanceof HTMLElement) frames.hidden = true;
    box.hidden = false;
    if (yes instanceof HTMLElement) yes.focus({ preventScroll: true });
    return new Promise((resolve) => {
      this.#askResolve = resolve;
    }).finally(() => {
      box.hidden = true;
      if (status) status.hidden = false;
      if (frames instanceof HTMLElement) frames.hidden = false;
    });
  }

  /** properties[Pet] in the buy box follows what we actually draw (ARVI 2026-10-19: the order shows the truth). */
  #setPetProperty(species, what = '') {
    const input = this.#root?.querySelector('input[name="properties[Pet]"]');
    if (!(input instanceof HTMLInputElement) || !species) return;
    const s = species === 'dog' || species === 'cat' ? species : what || species;
    input.value = s.charAt(0).toUpperCase() + s.slice(1);
  }

  /** text/plain beacon: no CORS preflight, survives the tab closing */
  #report(why, msg, secs) {
    try {
      const data = { why, msg, secs, job: this.#job?.id || '', design: this.#design, visitor: visitorId() };
      navigator.sendBeacon?.(`${this.#api}/report`, new Blob([JSON.stringify(data)], { type: 'text/plain' }));
    } catch {
      /* reporting must never break the studio */
    }
  }

  #fileUrl(n, kind) {
    return this.#job ? `${this.#api}/file/${this.#job.id}/sketch-${n}-${kind}.webp?t=${this.#job.t}` : '';
  }

  /**
   * ARVI 2026-10-19: the picked sketch rides along with the order as a JPEG file (properties[Your design]).
   * Shopify stores it on its CDN and shows a "Your design" link in checkout, order status, Orders and emails –
   * a plain URL shows as raw text, and replacing the line image (Cart Transform update) needs Shopify Plus.
   * Never blocks the cart: on any failure the input stays disabled and the order goes without it.
   * @param {string} url
   * @param {number} n
   */
  async #attachDesignFile(url, n) {
    const input = this.#q('[data-sk-design]');
    if (!(input instanceof HTMLInputElement) || !url || typeof DataTransfer === 'undefined') return;
    input.disabled = true;
    try {
      // no-store: the <img> already cached this file WITHOUT CORS headers (no Origin, no Vary) – a cached hit fails CORS
      const blob = await (await fetch(url, { cache: 'no-store' })).blob();
      const bmp = await createImageBitmap(blob);
      const scale = Math.min(1, 1400 / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bmp.width * scale);
      canvas.height = Math.round(bmp.height * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#fff';   // transparent edges → white, as on a white tee
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
      bmp.close();
      const jpg = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.88));
      if (!(jpg instanceof Blob) || this.#sketches[this.#picked - 1]?.n !== n) return;   // another sketch picked meanwhile
      const dt = new DataTransfer();
      dt.items.add(new File([jpg], `your-design-${this.#job?.id || 'sketch'}-${n}.jpg`, { type: 'image/jpeg' }));
      input.files = dt.files;
      input.disabled = false;
    } catch {
      /* the order still carries Sketch + _sketch_key; only the checkout link is missing */
    }
  }

  #renderGrid(grid) {
    if (!(grid instanceof HTMLElement)) return;
    grid.innerHTML = this.#sketches
      .map(
        (s, i) => `<button type="button" class="sk-pick" data-sk-pick="${i + 1}" aria-pressed="false" aria-label="Sketch ${i + 1}">
          <img src="${s.art}" alt="" width="400" height="500" loading="lazy">
          <b class="sk-n" aria-hidden="true">${i + 1}</b><span class="sk-ok" aria-hidden="true">Picked</span>
        </button>`
      )
      .join('');
  }

  #renderTries() {
    // live: never disabled – with no free round left the button opens the free account form
    const unlimited = this.#tries === Infinity;
    const label = unlimited ? 'unlimited with your account' : this.#tries > 0 ? '1 free sketch round' : 'more with a free account';
    for (const el of this.#all('[data-sk-tries]')) el.textContent = this.#live ? label : this.#tries > 0 ? `${this.#tries} free ${this.#tries === 1 ? 'try' : 'tries'} left` : 'No free tries left';
    // ARVI 2026-10-19: "Draw 3 more (free account)" read as 3 more rounds left – say what happens instead
    for (const el of this.#all('[data-sk-left]')) el.textContent = this.#live ? '' : `(${this.#tries} left)`;
    for (const b of this.#all('[data-sk-action="redraw"]')) {
      const t = b.firstChild;
      if (t && t.nodeType === Node.TEXT_NODE) t.textContent = this.#live && !unlimited && this.#tries <= 0 ? 'Draw 3 more – free account needed ' : 'Draw 3 more ';
    }
    for (const b of this.#all('[data-sk-action="redraw"], [data-sk-action="draw"]')) {
      if (b instanceof HTMLButtonElement) b.disabled = !this.#live && this.#tries <= 0;
    }
  }

  #pick(n, grid) {
    this.#picked = n;
    for (const b of this.#all('.sk-pick', grid || this.#root)) b.setAttribute('aria-pressed', String(Number(b.dataset.skPick) === n));
    const use = this.#q('[data-sk-action="use"]', this);
    if (use instanceof HTMLButtonElement) {
      use.disabled = false;
      use.textContent = `Use sketch ${n} on my tee →`;
    }
    if (this.#mode === 'a') this.#apply();
  }

  #colour() {
    const b = this.#root?.querySelector('[data-colour][aria-pressed="true"]');
    return (b && b.getAttribute('data-colour')) || 'White';
  }

  #position() {
    const p = this.#q('hom-print-position');
    return p && typeof p.setArt === 'function' ? p : null;
  }

  /** The picked sketch goes to the gallery, the order and the CTA line. */
  #apply() {
    const s = this.#sketches[this.#picked - 1];
    if (!s) return;
    const liveArt = this.#q('[data-live-art]');
    if (liveArt instanceof HTMLImageElement && liveArt.dataset.catalogue === undefined) liveArt.dataset.catalogue = liveArt.getAttribute('src') || '';
    const view = this.#q('[data-sk-view]');
    if (view instanceof HTMLElement) view.hidden = false;
    this.#view('sketch');

    const line = this.#q('[data-sk-attached]');
    if (line instanceof HTMLElement) line.hidden = false;
    const n = this.#q('[data-sk-picked-n]');
    if (n) n.textContent = String(this.#picked);
    const input = this.#q('[data-sk-input]');
    if (input instanceof HTMLInputElement) {
      input.disabled = false;
      input.value = this.#job ? `Sketch ${this.#picked} of ${this.#sketches.length}` : `Sketch ${this.#picked} of 3 (studio prototype)`;
    }
    const key = this.#q('[data-sk-key]');
    if (key instanceof HTMLInputElement && this.#job) {
      key.disabled = false;
      key.value = `${this.#job.id}/${s.n}`;
    }
    // the cart shows the client's own sketch as the line thumbnail
    const preview = this.#q('[data-sk-preview]');
    if (preview instanceof HTMLInputElement && this.#job) {
      preview.disabled = false;
      preview.value = this.#fileUrl(s.n, 'web');
    }
    // checkout / order status / emails: the sketch goes along as a file → Shopify shows a "Your design" link to it
    this.#attachDesignFile(this.#fileUrl(s.n, 'web'), s.n);
    const cur = this.#q('[data-sk-a] [data-sk-tries]');
    if (cur) cur.textContent = `Sketch ${this.#picked} ✓`;
    this.#q('.gal .main')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /** Put the picked sketch on the live garment: white version, or the black-tee version when Black is chosen. */
  #showArt() {
    const s = this.#sketches[this.#picked - 1];
    if (!s) return;
    const pos = this.#position();
    const liveArt = this.#q('[data-live-art]');
    if (!this.#job) {
      // demo: page B composites live; otherwise the pre-made tee photo goes over the gallery
      if (liveArt instanceof HTMLImageElement && s.cut) {
        if (pos) pos.setArt(s.cut);
        else liveArt.src = s.cut;
      } else {
        const fit = this.#all('[data-fit]').find((b) => b.getAttribute('aria-pressed') === 'true')?.getAttribute('data-fit') || 'Men';
        const overlay = this.#q('[data-sk-overlay]');
        if (overlay instanceof HTMLImageElement) {
          overlay.src = s.tee[fit] || s.tee.Men;
          overlay.hidden = false;
        }
      }
      return;
    }
    const black = this.#colour().toLowerCase() === 'black';
    const set = (src, blankColour = null) => (pos ? pos.setArt(src, blankColour) : liveArt instanceof HTMLImageElement && (liveArt.src = src));
    if (!black) return set(s.cut);
    if (this.#black[s.n] === 'done') return set(this.#fileUrl(s.n, 'black-web'));
    // the white-tee sketch has dark lettering – on a black blank it would vanish (ARVI 2026-10-16, The Maestro),
    // so until the black version is ready the sketch is shown on a white blank with a note
    this.#note(this.#black[s.n] === 'error' ? 'The black-tee preview comes with your final preview by email.' : 'Preparing the black-tee version of your sketch… shown on white meanwhile (about 40 s)');
    set(s.cut, 'white');
    this.#requestBlack(s.n);
  }

  async #requestBlack(n) {
    if (this.#black[n] === 'running') return;
    this.#black[n] = 'running';
    const { id, t } = this.#job;
    try {
      const r = await fetch(`${this.#api}/sketch/${id}/black?t=${t}&n=${n}`, { method: 'POST', signal: this.#ac.signal });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || 'black');
      }
    } catch (e) {
      this.#black[n] = 'error';
      return this.#note(e instanceof Error && e.message !== 'black' ? e.message : 'The black-tee preview comes with your final preview by email.');
    }
    const poll = async () => {
      try {
        const s = await (await fetch(`${this.#api}/sketch/${id}?t=${t}`, { signal: this.#ac.signal, cache: 'no-store' })).json();
        const st = s.black?.[String(n)];
        if (st === 'done' || st === 'error') {
          this.#black[n] = st;
          this.#note(st === 'done' ? '' : 'The black-tee preview comes with your final preview by email.');
          if (st === 'done' && this.#sketches[this.#picked - 1]?.n === n) this.#showArt();
          return;
        }
      } catch {
        /* try again */
      }
      this.#blackTimer = window.setTimeout(poll, POLL_MS);
    };
    this.#blackTimer = window.setTimeout(poll, POLL_MS * 3);
  }

  #note(msg) {
    const line = this.#q('[data-sk-attached]');
    if (!(line instanceof HTMLElement)) return;
    let el = this.#q('[data-sk-note]', line);
    if (!el) {
      el = document.createElement('small');
      el.dataset.skNote = '';
      el.className = 'sk-note';
      line.append(el);
    }
    el.textContent = msg ? ` · ${msg}` : '';
  }

  #view(mode) {
    const liveArt = this.#q('[data-live-art]');
    if (mode === 'sketch') this.#showArt();
    else if (liveArt instanceof HTMLImageElement && liveArt.dataset.catalogue !== undefined) {
      const pos = this.#position();
      if (pos) pos.setArt(liveArt.dataset.catalogue);
      else if (liveArt.dataset.catalogue) liveArt.src = liveArt.dataset.catalogue;
    }
    // the tee-photo overlay is only for the demo on pages without a live preview
    const overlay = this.#q('[data-sk-overlay]');
    if (overlay instanceof HTMLElement) overlay.hidden = mode !== 'sketch' || !!this.#job || liveArt instanceof HTMLImageElement;
    for (const b of this.#all('[data-sk-viewmode]')) b.setAttribute('aria-pressed', String(b.dataset.skViewmode === mode));
  }

  #email(btn) {
    btn.textContent = 'Sent (prototype) ✓';
    btn.disabled = true;
  }
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

if (!customElements.get('hom-sketch-studio')) {
  customElements.define('hom-sketch-studio', HomSketchStudio);
}
